/**
 * The platform console's Products sheet (migration 0227; plan of record
 * docs/plans/2026-10-13_microvision-and-the-account-layer.md, section A): which suite products an org holds and how
 * (trial, active or suspended, with an end, seats and a plan), granted, changed and removed here; and the org's opt-in
 * to its data training shared models, set on the record of its written agreement. Only a platform admin sees any of
 * it; the routes are RequirePlatformAdmin's. Foundry Twin is shown and not edited here: every org holds it.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, Loader2 } from "lucide-react";
import {
  platformAdminApi,
  type AdminModelTraining,
  type AdminOrgSuiteProduct,
  type OrgSummary,
} from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import { TWIN } from "@/lib/suite";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import {
  STATUS_WORDS,
  formOfProduct,
  holdingWords,
  orgProductsKey,
  productBody,
  productFormProblem,
  productsErrorWords,
  trainingWords,
  type ProductForm,
  type ProductStatus,
} from "./suite-products";

/** The Products column's cell: opens the org's products. Nothing for anyone but a platform admin. */
export function ProductsCell({ org, onOpen }: { org: Pick<OrgSummary, "name">; onOpen: () => void }) {
  const { isPlatformAdmin } = useOrg();
  if (!isPlatformAdmin) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 gap-1 px-2 text-xs"
      title={`Products of ${org.name}`}
      aria-label={`Products of ${org.name}`}
      onClick={onOpen}
    >
      <Boxes className="size-3.5" />
      Products
    </Button>
  );
}

function ProductEditor({
  org,
  product,
  onChanged,
}: {
  org: Pick<OrgSummary, "id" | "name">;
  product: AdminOrgSuiteProduct;
  onChanged: () => Promise<unknown>;
}) {
  const [form, setForm] = useState<ProductForm>(() => formOfProduct(product));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const problem = productFormProblem(form);
  const id = `product-${product.key}`;

  const save = async () => {
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      await platformAdminApi.putOrgProduct(org.id, product.key, productBody(form));
      await onChanged();
    } catch (err) {
      setError(productsErrorWords(err, org.name));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await platformAdminApi.deleteOrgProduct(org.id, product.key);
      setConfirmRemove(false);
      await onChanged();
    } catch (err) {
      setError(productsErrorWords(err, org.name));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-3"
      aria-label={product.name}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-status`}>
          <span className="font-medium text-on-surface">Status</span>
          <select
            id={`${id}-status`}
            value={form.status}
            disabled={busy}
            onChange={(e) => setForm({ ...form, status: e.target.value as ProductStatus })}
            className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-on-surface"
          >
            {(Object.keys(STATUS_WORDS) as ProductStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_WORDS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-until`}>
          <span className="font-medium text-on-surface">Ends on</span>
          <Input
            id={`${id}-until`}
            type="date"
            value={form.until}
            disabled={busy}
            onChange={(e) => setForm({ ...form, until: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-seats`}>
          <span className="font-medium text-on-surface">Seats</span>
          <Input
            id={`${id}-seats`}
            inputMode="numeric"
            placeholder="No limit"
            value={form.seats}
            disabled={busy}
            onChange={(e) => setForm({ ...form, seats: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-plan`}>
          <span className="font-medium text-on-surface">Plan</span>
          <Input
            id={`${id}-plan`}
            placeholder="Optional"
            value={form.plan}
            disabled={busy}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
          />
        </label>
      </div>
      <p className="text-xs text-on-surface-muted">
        Saving replaces how the org holds {product.name}: an empty field is cleared. A trial needs an end date.
      </p>
      {(problem || error) && (
        <p role="alert" className="text-sm text-danger">
          {error ?? problem}
        </p>
      )}
      {confirmRemove ? (
        <div className="flex flex-col gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm">
          <p className="text-on-surface">
            Remove {product.name} from {org.name}? Its members lose their roles in it.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmRemove(false)}>
              Keep it
            </Button>
            <Button type="button" variant="danger" size="sm" disabled={busy} onClick={() => void remove()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : `Remove ${product.name}`}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          {product.held && (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmRemove(true)}>
              Remove
            </Button>
          )}
          <Button type="submit" variant="primary" size="sm" disabled={busy || problem !== null}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : product.held ? "Save" : `Grant ${product.name}`}
          </Button>
        </div>
      )}
    </form>
  );
}

function TrainingSwitch({
  org,
  training,
  onChanged,
}: {
  org: Pick<OrgSummary, "id" | "name">;
  training: AdminModelTraining;
  onChanged: () => Promise<unknown>;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (on: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await platformAdminApi.setOrgModelTraining(org.id, on);
      setAsking(false);
      await onChanged();
    } catch (err) {
      setError(productsErrorWords(err, org.name));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-2" aria-label="Model training">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">Model training</h3>
      <p className="text-sm text-on-surface-muted">
        Whether {org.name}&apos;s data may train the shared models. Turn it on only on the record of the org&apos;s
        written agreement, which is kept outside the platform.
      </p>
      <p className="text-sm font-medium text-on-surface" data-training={training.opt_in ? "on" : "off"}>
        {trainingWords(training)}
      </p>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {asking ? (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-muted p-3 text-sm">
          <p className="text-on-surface">
            Is {org.name}&apos;s written agreement on record? Turning this on records you and the time.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setAsking(false)}>
              Not yet
            </Button>
            <Button type="button" variant="primary" size="sm" disabled={busy} onClick={() => void set(true)}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : "Yes, turn it on"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button
            type="button"
            variant={training.opt_in ? "ghost" : "secondary"}
            size="sm"
            disabled={busy}
            onClick={() => (training.opt_in ? void set(false) : setAsking(true))}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : training.opt_in ? "Turn off" : "Turn on"}
          </Button>
        </div>
      )}
    </section>
  );
}

/** The sheet: the org's products (GET), then each product's editor and the training switch. */
export function ProductsSheet({ org, onClose }: { org: Pick<OrgSummary, "id" | "name"> | null; onClose: () => void }) {
  const { isPlatformAdmin } = useOrg();
  const qc = useQueryClient();
  const products = useQuery({
    queryKey: orgProductsKey(org?.id ?? ""),
    queryFn: () => platformAdminApi.getOrgProducts(org?.id as string),
    enabled: org !== null && isPlatformAdmin,
    staleTime: 0,
    retry: false,
  });
  if (!org || !isPlatformAdmin) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: orgProductsKey(org.id) });
  return (
    <Sheet open onClose={onClose} ariaLabel={`Products of ${org.name}`} widthClass="max-w-lg">
      <SheetHeader title="Products" subtitle={org.name} onClose={onClose} />
      {products.data !== undefined ? (
        <div className="flex flex-col gap-6 p-5">
          <p className="text-sm text-on-surface-muted">
            The suite products {org.name} holds. Once it holds one, its admins give members access to it on the Team
            page.
          </p>
          {products.data.products.map((p) => (
            <section key={p.key} className="flex flex-col gap-3 border-t border-border pt-4" aria-label={p.name}>
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-sm font-semibold text-on-surface">{p.name}</h3>
                <span className="text-xs text-on-surface-muted" data-holding={p.key}>
                  {holdingWords(p)}
                </span>
              </div>
              {p.key === TWIN ? (
                <p className="text-xs text-on-surface-muted">Every organisation holds Foundry Twin.</p>
              ) : (
                <ProductEditor
                  // Starts again from the server's values after a change, its own or another admin's.
                  key={`${p.key}:${p.updated_at ?? "none"}:${p.held}`}
                  org={org}
                  product={p}
                  onChanged={refresh}
                />
              )}
            </section>
          ))}
          <div className="border-t border-border pt-4">
            <TrainingSwitch org={org} training={products.data.model_training} onChanged={refresh} />
          </div>
        </div>
      ) : products.isError ? (
        <p role="alert" className="p-5 text-sm text-danger">
          {productsErrorWords(products.error, org.name)}
        </p>
      ) : (
        <p className="flex items-center gap-2 p-5 text-sm text-on-surface-muted">
          <Loader2 className="size-4 animate-spin" /> Loading the products
        </p>
      )}
    </Sheet>
  );
}
